import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NbBadge, NbButton, NbDrawerShell, NbEmptyState, NbSkeleton } from '@nabotavle/ui';
import { VarslerService, type NotificationCentre, type NotificationView } from '@nabotavle/data-access';

/**
 * Varselskuff (2026-09 styre prototype, §5.2 pt 3): the topbar bell opens this
 * right-hand drawer with the board member's notification centre. Marking one or
 * all as read updates the list optimistically; the shell reloads its unread
 * count when the drawer closes. Deep links stay resident-app routes, so the
 * drawer only marks read — it does not navigate. Strings are `skuff.varsler.*`; the
 * importance enum renders through `varsler.importance.<value>` with the raw value as
 * fallback for unknown levels.
 */
@Component({
  selector: 'styre-varsler-drawer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, NbDrawerShell, NbBadge, NbButton, NbEmptyState, NbSkeleton, TranslatePipe],
  template: `<nb-drawer [kicker]="'skuff.varsler.kicker' | translate" [title]="'skuff.varsler.title' | translate" [closeLabel]="'common.close' | translate">
    @if (loading()) {
      <nb-skeleton height="56px" /><nb-skeleton height="56px" style="margin-top:10px" />
    } @else if (centre(); as c) {
      @if (!c.nye.length && !c.tidligere.length) {
        <nb-empty-state [title]="'skuff.varsler.emptyTitle' | translate">{{ 'skuff.varsler.emptyBody' | translate }}</nb-empty-state>
      } @else {
        @if (c.nye.length) {
          <div class="over">{{ 'skuff.varsler.new' | translate }}</div>
          @for (n of c.nye; track n.id) {
            <button type="button" class="row unread" (click)="markRead(n)">
              <span class="dot" aria-hidden="true"></span>
              <span class="nb-sr-only">{{ 'skuff.varsler.unread' | translate }}</span>
              <span class="txt"><span class="t">{{ n.title }}</span><span class="b">{{ n.body }}</span><span class="m">{{ n.createdAtUtc | date: 'd. MMM HH:mm' }}</span></span>
              @if (n.importance !== 'info') { <nb-badge [variant]="n.importance === 'kritisk' ? 'danger' : 'warning'">{{ importanceLabel(n.importance) }}</nb-badge> }
            </button>
          }
        }
        @if (c.tidligere.length) {
          <div class="over">{{ 'skuff.varsler.earlier' | translate }}</div>
          @for (n of c.tidligere; track n.id) {
            <div class="row">
              <span class="txt"><span class="t">{{ n.title }}</span><span class="b">{{ n.body }}</span><span class="m">{{ n.createdAtUtc | date: 'd. MMM HH:mm' }}</span></span>
            </div>
          }
        }
      }
    }
    @if (centre()?.nye?.length) {
      <nb-button nbDrawerFooter variant="sec" size="sm" (click)="markAll()">{{ 'skuff.varsler.markAll' | translate }}</nb-button>
    }
  </nb-drawer>`,
  styles: `
    .over { font-size: 11px; font-weight: 800; letter-spacing: 0.14em; text-transform: uppercase; color: var(--muted); margin: 6px 0 8px; }
    .over + .over { margin-top: 18px; }
    .row { display: flex; align-items: flex-start; gap: 10px; inline-size: 100%; text-align: start; padding: 12px 10px; border-radius: var(--r-md); border: 0; background: none; font: inherit; color: inherit; }
    button.row { cursor: pointer; }
    button.row:hover { background: var(--surface-2); }
    button.row:focus-visible { outline: none; box-shadow: var(--ring); }
    .row + .row { border-block-start: 1px solid var(--border); border-radius: 0; }
    .dot { inline-size: 8px; block-size: 8px; border-radius: 50%; background: var(--brand); margin-block-start: 7px; flex-shrink: 0; }
    .txt { flex: 1; min-inline-size: 0; display: flex; flex-direction: column; gap: 2px; }
    .t { font-weight: 700; font-size: 14.5px; }
    .unread .t { font-weight: 800; }
    .b { font-size: 13.5px; color: var(--text-2); }
    .m { font-size: 12px; color: var(--faint); font-weight: 600; }
  `,
})
export class VarslerDrawer {
  private readonly api = inject(VarslerService);
  private readonly t = inject(TranslateService);
  protected readonly centre = signal<NotificationCentre | null>(null);
  protected readonly loading = signal(true);

  /** Importance labels for the active locale; recomputed on a switch. */
  private readonly importanceLabels = computed<Record<string, string>>(() => {
    this.t.currentLang();
    return Object.fromEntries(['info', 'viktig', 'kritisk'].map((k) => [k, this.t.instant(`varsler.importance.${k}`)]));
  });

  /** `varsler.importance.<value>` — falls back to the raw enum value when no translation exists. */
  protected importanceLabel(value: string): string {
    const label = this.importanceLabels()[value];
    return label && label !== `varsler.importance.${value}` ? label : value;
  }

  constructor() {
    this.api.centre().subscribe({
      next: (c) => { this.centre.set(c); this.loading.set(false); },
      error: () => { this.centre.set({ nye: [], tidligere: [] }); this.loading.set(false); },
    });
  }

  protected markRead(n: NotificationView): void {
    this.centre.update((c) => c && { nye: c.nye.filter((x) => x.id !== n.id), tidligere: [{ ...n, unread: false }, ...c.tidligere] });
    this.api.markRead(n.id).subscribe({ error: () => { /* the next open re-fetches */ } });
  }

  protected markAll(): void {
    this.centre.update((c) => c && { nye: [], tidligere: [...c.nye.map((x) => ({ ...x, unread: false })), ...c.tidligere] });
    this.api.markAllRead().subscribe({ error: () => { /* the next open re-fetches */ } });
  }
}
